import { useEffect, useState } from "react";
import app from "../components/app";
import config from "../config.json";


let firstLoad = false;

const VIBESDK_BASE_URL = config.vibesdkURL;
// Must match EMBED_SHARED_SECRET on the vibesdk worker. 64 hex chars (32 bytes).
const VIBESDK_SECRET = "0123456789abcdef0123456789abcdef0123456789abcdef0123456789abcdef";
const TOKEN_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

function hexToBytes(hex) {
    const clean = hex.trim();
    if (clean.length !== 64) throw new Error("Secret must be 64 hex chars (32 bytes)");
    if (!/^[0-9a-fA-F]+$/.test(clean)) throw new Error("Secret must be hex only");
    const out = new Uint8Array(32);
    for (let i = 0; i < 32; i++) out[i] = parseInt(clean.substr(i * 2, 2), 16);
    return out;
}

function bytesToBase64url(bytes) {
    let bin = "";
    for (const b of bytes) bin += String.fromCharCode(b);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function buildToken({ userId, email, pageId, appId, secretHex, ttlMs }) {
    const claims = {
        userId,
        email,
        exp: Date.now() + ttlMs
    };
    // pageId / appId are resume claims — encrypt them INSIDE the token so
    // the worker resolves them server-side. pageId is the resume key:
    // reopening the iframe with the same pageId resumes the existing chat.
    if (pageId) claims.pageId = pageId;
    if (appId) claims.appId = appId;
    const payload = JSON.stringify(claims);

    const keyBytes = hexToBytes(secretHex);
    const key = await crypto.subtle.importKey(
        "raw", keyBytes, { name: "AES-GCM" }, false, ["encrypt"]
    );

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ctBuf = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        new TextEncoder().encode(payload)
    );
    // Web Crypto encrypt output = ciphertext || authTag (16 bytes)

    const blob = new Uint8Array(12 + ctBuf.byteLength);
    blob.set(iv, 0);
    blob.set(new Uint8Array(ctBuf), 12);
    return bytesToBase64url(blob);
}

async function buildVibesdkUrl({ userId, email, pageId, appid, ttlMs = TOKEN_TTL_MS, baseUrl = VIBESDK_BASE_URL }) {
    const token = await buildToken({
        userId,
        email,
        pageId,
        appId: appid,
        secretHex: VIBESDK_SECRET,
        ttlMs
    });
    // pageId / appid now live INSIDE the encrypted token — the only URL
    // param is the token itself.
    const params = new URLSearchParams({ token });
    return `${baseUrl.replace(/\/$/, "")}/?${params.toString()}`;
}

// The delete-app endpoint accepts the same embed token as the Create Feature
// iframe, so no new credential is involved. The API docs ask for a short
// lifetime (5–10 min) — the iframe's 2 hours is far longer than a single
// request needs, so this keeps its own shorter TTL.
const DELETE_TOKEN_TTL_MS = 10 * 60 * 1000;

/**
 * Archive the vibesdk app bound to a PWA page.
 *
 * Call when a page hosting an embedded vibesdk feature is deleted in the
 * builder, so the underlying project is archived alongside it.
 *
 *   DELETE {vibesdkURL}/api/delete-app/{pageId}
 *   X-Embed-Token: <AES-256-GCM embed token>
 *
 * The app is resolved by (userId from the token, pageId from the URL), so a
 * caller can only ever delete their own app — another user's pageId returns
 * 404. Deletion is a soft archive on the vibesdk side, not a hard delete.
 *
 * Never rejects: callers are fire-and-forget, and an unhandled rejection here
 * would surface as a Sentry error for what is a best-effort cleanup. Returns a
 * result object instead.
 *
 * @param {string} pageId  The PWA page id the app was created under.
 * @returns {Promise<{ok: boolean, status?: number, alreadyGone?: boolean, reason?: string, message?: string}>}
 */
export async function vibeDeletePage(pageId) {
    if (!pageId) {
        console.warn("vibeDeletePage: called with no pageId, ignoring");
        return { ok: false, reason: "no-pageId" };
    }

    const userId = localStorage.getItem("userId");
    const email  = localStorage.getItem("userEmail");
    // userId and email are both required in the token payload.
    if (!userId || !email) {
        console.warn("vibeDeletePage: no userId/userEmail in localStorage, cannot mint embed token");
        return { ok: false, reason: "no-identity" };
    }

    try {
        const token = await buildToken({
            userId,
            email,
            pageId,
            appId: localStorage.getItem("aiAppId") || undefined,
            secretHex: VIBESDK_SECRET,
            ttlMs: DELETE_TOKEN_TTL_MS
        });

        const base = VIBESDK_BASE_URL.replace(/\/$/, "");
        const res = await fetch(`${base}/api/delete-app/${encodeURIComponent(pageId)}`, {
            method: "DELETE",
            headers: { "X-Embed-Token": token }
        });

        // 404 = no app for this (user, pageId), including one already deleted.
        // The contract calls that "already gone" — a success for our purposes,
        // and explicitly not worth retrying.
        if (res.status === 404) {
            return { ok: true, alreadyGone: true, status: 404 };
        }

        if (!res.ok) {
            let message = "";
            try {
                const body = await res.json();
                message = (body && (body.message || (body.error && body.error.message))) || "";
            } catch (_) { /* non-JSON error body */ }
            console.warn("vibeDeletePage: delete failed", res.status, message);
            return { ok: false, status: res.status, message };
        }

        return { ok: true, status: res.status };
    }
    catch (err) {
        console.warn("vibeDeletePage: request error", err);
        return { ok: false, reason: "request-failed" };
    }
}

const files = {
  "jsFiles":[],
  "moduleFiles":[
    {
    "id": "appkit-index-js",
    "file":"index.js"
    }
  ],
  "cssFiles":[
    {
      "id": "appkit-index-css",
      "file":"index.css"
    }
  ]
}

function loadModuleScript(src) {
  return new Promise((resolve, reject) => {
    // prevent duplicate load
    if (document.querySelector("#" + src.id)) {
      resolve("already loaded");
      return;
    }


    const script = document.createElement("script");
    script.type = "module";
    script.id = src.id;
    script.crossOrigin = "anonymous";
    script.src = `${config.appkitPath}/${src.file}?version=${window.aiVersion}`;

    script.onload = () => resolve("loaded");
    script.onerror = () => reject(new Error("Failed to load script"));

    document.head.appendChild(script);
  });
}

function loadCss(href) {
  return new Promise((resolve, reject) => {
    // prevent duplicate load
   if (document.querySelector("#" + href.id)) {
      resolve("already loaded");
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.id = href.id;
    link.href = `${config.appkitPath}/${href.file}?version=${window.aiVersion}`;

    link.onload = () => resolve("loaded");
    link.onerror = () => reject(new Error(`Failed to load CSS: ${href.file}`));
    document.head.appendChild(link);
  });
}

function loadAssets(css, js) {
  return Promise.all([
    ...css.map(function(v){
        return loadCss(v);
    }),
    ...js.map(function(v){
        return loadModuleScript(v);
    }),
  ]);
}

export default function Plugin() {
    const [src, setSrc] = useState("");

    // Tag #ai-editor with a scope class while this component is mounted.
    // Skip on localhost so dev sessions don't inherit prod-only styling.
    useEffect(() => {
        if (app.isLocalhost()) return;
        const root = document.getElementById("ai-editor");
        if (root) root.classList.add("plugin-editor");
        return function(){
            const el = document.getElementById("ai-editor");
            if (el) el.classList.remove("plugin-editor");
        };
    }, []);

    useEffect(() => {
        const userId = localStorage.getItem("userId");
        const email = localStorage.getItem("userEmail");
        const appid = localStorage.getItem("aiAppId");
        const pageId = localStorage.getItem("aiPageId");

        var isHttps = window.location.protocol === 'https:';
        var attrs = 'path=/; max-age=' + 60 * 60 * 24 * 365;
        attrs += isHttps ? '; Secure; SameSite=None' : '; SameSite=Lax';
        document.cookie = 'userEmail=' + encodeURIComponent(email) + '; ' + attrs;

        // Provision the plugin server-side before mounting the vibesdk iframe.
        app.setupPlugins(email)
            .catch(function(e){ console.error("setup-plugins failed", e); })
            .finally(function(){
                buildVibesdkUrl({
                    userId,
                    email,
                    pageId,
                    appid
                }).then(function(url){
                    setSrc(url);
                }).catch(function(e){
                    console.error("vibesdk iframe url build failed", e);
                });
            });

        // Fullscreen toggle: vibesdk posts { type: 'vibesdk:preview-fullscreen',
        // active: bool } from inside the iframe. Save the iframe's + body's
        // original inline styles on activate, restore exactly on deactivate.
        let savedIframeCss = null;
        let savedBodyOverflow = null;
        const onMessage = function(e){
            if (!e.data) return;

            // Save trigger: vibesdk posts { type: "vibesdk:save-app" } when the
            // user clicks Save inside the iframe. We synthesize a click on the
            // parent's own Save button so the existing publish flow runs
            // (mirrors the saveAppkit handler in src/pages/divKit.js).
            if (e.data.type === "vibesdk:save-app") {
                if (localStorage.getItem("showSaveModal") === "1") {
                    document.querySelector(".btn.btn-success.publish-btn.startTrialModal")?.click();
                } else {
                    document.querySelector(".btn.btn-success.publish-btn")?.click();
                }
                return;
            }

            if (e.data.type !== "vibesdk:preview-fullscreen") return;
            const iframe = document.getElementById("iframe");
            if (!iframe) return;

            if (e.data.active) {
                if (savedIframeCss === null) savedIframeCss = iframe.style.cssText;
                if (savedBodyOverflow === null) savedBodyOverflow = document.body.style.overflow;
                iframe.style.cssText = "position:fixed;inset:0;width:100vw;height:100vh;z-index:9999;border:0;";
                document.body.style.overflow = "hidden";
            } else {
                iframe.style.cssText = savedIframeCss || "";
                document.body.style.overflow = savedBodyOverflow || "";
                savedIframeCss = null;
                savedBodyOverflow = null;
            }
        };
        window.addEventListener("message", onMessage);
        return function(){
            window.removeEventListener("message", onMessage);
            document.body.style.overflow = "";
        };
    }, []);

    return <div id="plugin-area" className="w-100 h-100 overflow-hidden">
        {src?<iframe id="iframe" src={src} style={{width:"100%", height:"100%"}} frameBorder="0" allow="microphone" allowFullScreen sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms" />:""}
    </div>
}
